import { FC, useState } from 'react';
import { CreditCard } from 'lucide-react';
import { Button } from '@/components/ui';
import { useBilling } from '../hooks/useBilling';
import { useToast } from '@/contexts/ToastContext';
import api from '@/utils/api';
import PlanSelectionModal from '@/components/planModel/planModel';

interface BillingTabProps {
    initialFetch: boolean;
    userStatus?: string;
}

export const BillingTab: FC<BillingTabProps> = ({ initialFetch, userStatus }) => {
    const { billing, isBillingLoading, error, fetchBillingSummary } = useBilling(initialFetch);
    const [openModal, setOpenModal] = useState(false);
    const { addToast } = useToast();

    const handleBillingPortalRedirect = async () => {
        try {
            const { data } = await api.post('/subscriptions/create-billing-portal-session');

            if (data?.manualMode) {
                addToast('You are on a manual invoicing plan. Please contact support.', 'info');
                return;
            }

            if (data?.url) {
                window.location.href = data.url;
            } else {
                console.error('No billing portal URL returned');
                addToast('Unable to open billing portal. Please try again.', 'error');
            }
        } catch (err: unknown) {
            console.error('Failed to open billing portal:', err);
            addToast('Failed to open billing portal. Please try again.', 'error');
        }
    };

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-lg font-semibold text-gray-900">Billing</h2>
                <p className="text-sm text-gray-500 mt-1">Manage your billing and subscription</p>
            </div>

            {/* Loading State */}
            {isBillingLoading && (
                <div className="text-center py-12 text-gray-500">
                    <div className="flex justify-center items-center space-x-2">
                        <div className="w-6 h-6 border-4 border-t-4 border-gray-300 border-solid rounded-full animate-spin border-t-blue-500"></div>
                        <span>Loading billing information...</span>
                    </div>
                </div>
            )}

            {/* Demo Account Message */}
            {!isBillingLoading && !billing && error && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                    <p className="text-sm text-gray-500">{error}</p>
                </div>
            )}

            {/* If billing data is available */}
            {!isBillingLoading && billing && (
                <>
                    {/* Current Plan */}
                    <div className="bg-white border border-gray-200 rounded-lg">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h3 className="text-base font-semibold text-gray-900">Current Plan</h3>
                        </div>
                        <div className="p-6 flex items-center justify-between">
                            <div>
                                <div className="flex items-center gap-2">
                                    <p className="text-lg font-semibold text-gray-900">
                                        {billing?.plan.name || 'Free Plan'}
                                    </p>
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${billing.subscription.status === 'active' || billing.subscription.status === 'trialing' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                                        }`}>
                                        {billing.subscription.status}
                                    </span>
                                </div>
                                {billing?.subscription.currentPeriodEnd && (
                                    <p className="text-sm text-gray-500 mt-1">
                                        {billing.subscription.status === 'active' ? 'Renews on' : 'Ends on'} {new Date(billing.subscription.currentPeriodEnd).toLocaleDateString()}
                                    </p>
                                )}
                            </div>
                            <div className="text-right">
                                <p className="text-2xl font-bold text-gray-900">
                                    ${billing?.plan.amount.toLocaleString()}
                                </p>
                                {userStatus === 'active' && (
                                    <p className="text-sm text-gray-500">per {billing?.plan.interval}</p>
                                )}
                            </div>
                        </div>

                        {/* Upgrade button for inactive/canceled/past_due users who still have a billing record */}
                        {(billing.subscription.status === 'inactive' || billing.subscription.status === 'canceled' || billing.subscription.status === 'past_due') && (
                            <div className="px-6 pb-6 pt-2 border-t border-gray-50">
                                <Button variant="secondary" size="md" onClick={() => setOpenModal(true)}>
                                    Refresh Subscription
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Payment Method */}
                    {billing.paymentMethod && (
                        <div className="bg-white border border-gray-200 rounded-lg">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h3 className="text-base font-semibold text-gray-900">Payment Method</h3>
                            </div>
                            <div className="p-6">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <CreditCard className="w-6 h-6 text-gray-600" />
                                        <div>
                                            <p className="text-sm text-gray-900 capitalize">
                                                {billing.paymentMethod.brand} •••• {billing.paymentMethod.last4}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                Expires {billing.paymentMethod.exp_month}/{billing.paymentMethod.exp_year}
                                            </p>
                                        </div>
                                    </div>
                                    {billing.paymentMethod.id !== 'manual' && (
                                        <Button variant="secondary" size="sm" onClick={handleBillingPortalRedirect}>
                                            Update
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Billing History */}
                    {billing.billingHistory.length > 0 && (
                        <div className="bg-white border border-gray-200 rounded-lg">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h3 className="text-base font-semibold text-gray-900">Billing History</h3>
                            </div>
                            <div className="p-6">
                                <div className="h-64 overflow-y-auto">
                                    <table className="min-w-full text-sm">
                                        <thead className="text-gray-500">
                                            <tr>
                                                <th className="text-left py-2">Invoice #</th>
                                                <th className="text-left py-2">Date</th>
                                                <th className="text-left py-2">Amount</th>
                                                <th className="text-left py-2">Status</th>
                                                <th className="text-left py-2">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-gray-700">
                                            {billing.billingHistory.map((item) => (
                                                <tr key={item.id} className="border-t border-gray-100">
                                                    <td className="py-3">{item.number}</td>
                                                    <td className="py-3">{new Date(item.created * 1000).toLocaleDateString()}</td>
                                                    <td className="py-3">
                                                        ${(item.amount_paid).toLocaleString()} {item.currency.toUpperCase()}
                                                    </td>
                                                    <td className="py-3 capitalize">{item.status}</td>
                                                    <td className="py-3">
                                                        {item.invoice_pdf && (
                                                            <a
                                                                href={item.invoice_pdf}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-blue-600 hover:underline text-sm"
                                                            >
                                                                View PDF
                                                            </a>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Fallback when NO billing data is available AT ALL */}
            {!isBillingLoading && !billing && (
                <div className="bg-white border border-gray-200 rounded-lg">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-base font-semibold text-gray-900">Current Plan</h3>
                    </div>
                    <div className="p-6 flex items-center justify-between">
                        <div>
                            <p className="text-lg font-semibold text-gray-900">No Active Subscription</p>
                            <p className="text-sm text-gray-500 mt-1">Upgrade to unlock full features</p>
                        </div>
                        <div className="text-right">
                            <p className="text-2xl font-bold text-gray-900">Free</p>
                        </div>
                    </div>
                    <div className="px-6 pb-6">
                        <Button variant="secondary" size="md" onClick={() => setOpenModal(true)}>
                            Subscribe Now
                        </Button>
                    </div>
                </div>
            )}

            {openModal && <PlanSelectionModal
                isOpen={openModal}
                onClose={() => setOpenModal(false)}
                onPaymentSuccess={() => {
                    setOpenModal(false);
                    fetchBillingSummary();
                }}
            />}
        </div>
    );
};
